package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.clearFocusOnTapOutside
import com.partejar.app.core.ui.components.PartejarIconButton
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarDateField
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.components.rememberBringIntoViewOnFocusModifier
import com.partejar.app.core.ui.components.rememberFocusedFieldBoundsModifier
import com.partejar.app.core.ui.components.rememberTapOutsideFocusState
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.relacionamentos.data.NewRelationship
import com.partejar.app.feature.gestantes.data.GestanteAddress
import com.partejar.app.core.ui.domain.address.PatientAddressFields
import com.partejar.app.feature.relacionamentos.data.RelacionamentoFilter
import com.partejar.app.feature.relacionamentos.data.RelacionamentoStage
import com.partejar.app.feature.relacionamentos.data.RelationshipItem
import org.koin.compose.koinInject

@Composable
fun NovoRelacionamentoScreen(
    onBack: () -> Unit,
    onCreated: (Long) -> Unit,
    viewModel: NovoRelacionamentoViewModel = koinInject()
) {
    var name by remember { mutableStateOf("") }
    var baby by remember { mutableStateOf("") }
    var maternity by remember { mutableStateOf("") }
    var dueDate by remember { mutableStateOf("") }
    var attemptedSave by remember { mutableStateOf(false) }
    var address by remember { mutableStateOf(GestanteAddress()) }
    var origin by remember { mutableStateOf<String?>(null) }

    val submitting by viewModel.submitting.collectAsState()
    val error by viewModel.error.collectAsState()
    val focusManager = LocalFocusManager.current
    val babyFocus = remember { FocusRequester() }
    val maternityFocus = remember { FocusRequester() }
    val tapOutsideFocus = rememberTapOutsideFocusState()
    val nameFocusModifier = rememberFocusedFieldBoundsModifier(tapOutsideFocus)
        .then(rememberBringIntoViewOnFocusModifier(error))
    val babyFocusModifier = Modifier
        .focusRequester(babyFocus)
        .then(rememberFocusedFieldBoundsModifier(tapOutsideFocus))
        .then(rememberBringIntoViewOnFocusModifier(error))
    val maternityFocusModifier = Modifier
        .focusRequester(maternityFocus)
        .then(rememberFocusedFieldBoundsModifier(tapOutsideFocus))
        .then(rememberBringIntoViewOnFocusModifier(error))

    Scaffold(
        topBar = {
            PartejarTopBar(
                title = "Novo contato",
                leadingContent = {
                    PartejarIconButton(onClick = onBack) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .clearFocusOnTapOutside(tapOutsideFocus)
                .imePadding()
                .verticalScroll(rememberScrollState())
                .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                .padding(PartejarTheme.spacing.space16),
            verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
        ) {
            Text("Inicie a captação com o essencial.", style = MaterialTheme.typography.bodyMedium)

            PartejarTextField(
                value = name,
                onValueChange = { name = it },
                label = { Text("Nome da gestante") },
                modifier = Modifier.fillMaxWidth().then(nameFocusModifier),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                keyboardActions = KeyboardActions(onNext = { babyFocus.requestFocus() })
            )

            PartejarTextField(
                value = baby,
                onValueChange = { baby = it },
                label = { Text("Nome do bebê (opcional)") },
                modifier = Modifier.fillMaxWidth().then(babyFocusModifier),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                keyboardActions = KeyboardActions(onNext = { maternityFocus.requestFocus() })
            )

            PartejarTextField(
                value = maternity,
                onValueChange = { maternity = it },
                label = { Text("Maternidade (opcional)") },
                modifier = Modifier.fillMaxWidth().then(maternityFocusModifier),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus() })
            )

            PartejarDateField(
                value = dueDate,
                onValueChange = { dueDate = it },
                label = "DPP (opcional)",
                enabled = !submitting,
                error = if (attemptedSave && dueDate.isNotBlank() && parseBrazilianDate(dueDate) == null)
                    "Informe uma DPP válida." else null
            )

            RelationshipOriginField(origin, { origin = it }, enabled = !submitting)
            Text("Endereço (opcional)", style = MaterialTheme.typography.titleMedium)
            PatientAddressFields(address, { address = it }, enabled = !submitting)

            error?.let {
                PartejarAlert("Não foi possível criar", PartejarAlertTone.ERROR) { Text(it) }
            }

            PartejarButton(
                text = "Criar contato",
                onClick = {
                    attemptedSave = true
                    val parsedDueDate = dueDate.takeIf(String::isNotBlank)?.let(::parseBrazilianDate)
                    if (dueDate.isNotBlank() && parsedDueDate == null) return@PartejarButton
                    val newRelationship = NewRelationship(
                        name = name,
                        babyName = baby.ifBlank { null },
                        maternity = maternity.ifBlank { null },
                        origin = origin,
                        address = address,
                        dueDate = parsedDueDate?.toString()
                    )
                    viewModel.create(newRelationship, onCreated)
                },
                isLoading = submitting,
                enabled = name.isNotBlank()
            )
        }
    }
}

@Preview(widthDp = 390, heightDp = 844)
@Composable
private fun RelPreview() = PartejarTheme(ThemeMode.LIGHT) {
    RelacionamentosContent(
        state = RelacionamentosUiState.Content(
            listOf(
                RelationshipItem(
                    id = 1,
                    name = "Mariana Alves",
                    babyName = "Alice",
                    summary = "32s 2d",
                    maternity = "Hospital X",
                    stage = RelacionamentoStage.WAITING,
                    needsAttention = true,
                    nextMeeting = null,
                    nextFollowUp = null
                )
            )
        ),
        filter = RelacionamentoFilter.ALL,
        isDark = false,
        onThemeToggle = {},
        onFilter = {},
        onRetry = {},
        onLoadNextPage = {},
        onOpenDetail = {},
        onNew = {}
    )
}
